import { v4 as uuidV4 } from 'uuid';
import {
  AirportLocationSchemaType,
  BaseAccommodationLegSchemaValues,
  BaseCommercialAviaLegSchemaValues,
  BaseCurriServiceGroundSchemaValues,
  BaseSelfServicedGroundSchemaValues,
  BaseSupplementalLiftAviaSchemaValues,
  BaseTmdxAffiliateGroundSchemaValues,
  BaseTmdxAviaLegSchemaValues,
  BaseTmdxDriveAffiliateGroundSchemaValues,
  BaseTmdxDriveTedsGroundSchemaValues,
  BaseTxCenterGroundSchemaValues,
  BaseTxProviderAviaSchemaValues,
  CommonLegResourcesSchemaValues,
  CommonRouteLegSchemaValues,
  CustomAddressSchemaType,
  LocationSchemaType,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  LegLocationTypeEnum,
  LegTypeEnum,
  RouteLegTypeEnum,
  SegmentTypeEnum,
  GroundVehicleType,
} from '@/api/apis/route';

// LOCATIONS
export const DEFAULT_LOCATION_VALUES = {
  id: '',
  displayName: '',
  timezone: 'America/New_York',
  latitude: null,
  longitude: null,
};

export const DEFAULT_AIRPORT_LOCATION_VALUES: AirportLocationSchemaType = {
  ...DEFAULT_LOCATION_VALUES,
  locationType: LegLocationTypeEnum.airport,
  locationTypeId: LegLocationTypeEnum.airport,
  maoId: '',
  fboId: '',
  fboAddressId: 0,
};

export const DEFAULT_FACILITY_LOCATION_VALUES: LocationSchemaType = {
  ...DEFAULT_LOCATION_VALUES,
  locationType: LegLocationTypeEnum.hub,
  locationTypeId: LegLocationTypeEnum.hub,
};

export const DEFAULT_CUSTOM_LOCATION_VALUES: CustomAddressSchemaType = {
  ...DEFAULT_LOCATION_VALUES,
  locationType: LegLocationTypeEnum.custom,
  locationTypeId: LegLocationTypeEnum.custom,
  hereMapsId: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
  addressLine: '',
  latitude: null,
  longitude: null,
};

// FORMS
export const DEFAULT_LEG_VALUES: Pick<
  CommonRouteLegSchemaValues,
  'id' | 'formType' | 'legType' | 'segmentType' | 'groupId' | 'comments' | 'minDate' | 'maxDate'
> = {
  id: '',
  formType: LegTypeEnum.ground,
  legType: RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
  segmentType: SegmentTypeEnum.custom,
  groupId: uuidV4(),
  comments: '',
  minDate: null,
  maxDate: null,
};

export const DEFAULT_ROUTE_LEG_VALUES: Pick<
  CommonRouteLegSchemaValues,
  | 'departureLocation'
  | 'arrivalLocation'
  | 'departureDateTimeValue'
  | 'departureDateTime'
  | 'departureLocalTimezone'
  | 'arrivalDateTimeValue'
  | 'arrivalDateTime'
  | 'arrivalLocalTimezone'
  | 'isDateTimeLinked'
  | 'timeDurationInMs'
> = {
  departureLocation: DEFAULT_AIRPORT_LOCATION_VALUES,
  arrivalLocation: DEFAULT_AIRPORT_LOCATION_VALUES,
  departureDateTimeValue: null,
  departureDateTime: '',
  departureLocalTimezone: '',
  arrivalDateTimeValue: null,
  arrivalDateTime: '',
  arrivalLocalTimezone: '',
  isDateTimeLinked: true,
  timeDurationInMs: 0,
};

export const DEFAULT_LEG_RESOURCES_VALUES: Pick<CommonLegResourcesSchemaValues, 'resources' | 'hasSCS'> = {
  resources: [],
  hasSCS: false,
};

export const DEFAULT_AVIA_CREW_VALUES: Pick<
  BaseSupplementalLiftAviaSchemaValues | BaseTxProviderAviaSchemaValues,
  'crewMembers'
> = {
  crewMembers: [
    { firstName: '', lastName: '', phoneNumber: '' },
    { firstName: '', lastName: '', phoneNumber: '' },
  ],
};

export const DEFAULT_GROUND_LEG_VALUES = {
  formType: LegTypeEnum.ground,
  driver: '',
  email: '',
  phoneNumber: '',
  plateNumber: '',
  vehicleType: GroundVehicleType.SUV,
  reservationNumber: '',
  pickup: '',
  dropoff: '',
  wait: '',
  stay: '',
  distanceInMeters: 0,
  isFeasibleForDrive: true,
};

// AVIA FORMS
export const DEFAULT_TMDX_AVIA_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  formType: LegTypeEnum.air,
  legType: RouteLegTypeEnum.TmdxJetAviaLeg,
  selectedAircraftId: '',
} as BaseTmdxAviaLegSchemaValues;

export const DEFAULT_SUPPLEMENTAL_LIFT_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  ...DEFAULT_AVIA_CREW_VALUES,
  formType: LegTypeEnum.air,
  legType: RouteLegTypeEnum.SupplementalLiftAviaLeg,
  supplementalLiftProviderId: '',
  priceInCents: '',
  tailNumber: '',
} as BaseSupplementalLiftAviaSchemaValues;

export const DEFAULT_TX_CENTER_AVIA_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  ...DEFAULT_AVIA_CREW_VALUES,
  formType: LegTypeEnum.air,
  legType: RouteLegTypeEnum.TxCenterProviderAviaLeg,
  txCenterProviderId: '',
  tailNumber: '',
} as BaseTxProviderAviaSchemaValues;

export const DEFAULT_COMMERCIAL_AVIA_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  formType: LegTypeEnum.air,
  legType: RouteLegTypeEnum.CommercialFlightAviaLeg,
  segmentType: SegmentTypeEnum.custom,
  flightNumber: '',
  priceInCents: '',
  bookingNumber: '',
  connectedLegs: [],
  commercialFlight: undefined,
  sabreGroupId: '',
  seatType: '',
  flightOperator: '',
  numberOfSeats: 1,
} as BaseCommercialAviaLegSchemaValues;

// GROUND FORMS
export const DEFAULT_GROUND_FORM_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  ...DEFAULT_GROUND_LEG_VALUES,
};

export const DEFAULT_TMDX_AFFILIATE_VALUES = {
  ...DEFAULT_GROUND_FORM_VALUES,
  legType: RouteLegTypeEnum.TmdxAffiliateGroundLeg,
} as BaseTmdxAffiliateGroundSchemaValues;

export const DEFAULT_TMDX_DRIVE_AFFILIATE_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  formType: LegTypeEnum.ground,
  legType: RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
  distanceInMeters: 0,
  isFeasibleForDrive: true,
  tripId: null,
} as BaseTmdxDriveAffiliateGroundSchemaValues;

export const DEFAULT_TX_CENTER_GROUND_VALUES = {
  ...DEFAULT_GROUND_FORM_VALUES,
  legType: RouteLegTypeEnum.TxCenterProviderGroundLeg,
  txCenterProviderId: '',
} as BaseTxCenterGroundSchemaValues;

export const DEFAULT_DEVICE_DELIVERY_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  formType: LegTypeEnum.ground,
  legType: RouteLegTypeEnum.CurriServiceGroundLeg,
  distanceInMeters: 0,
  trackingNumber: '',
  isDateTimeLinked: false,
} as BaseCurriServiceGroundSchemaValues;

export const DEFAULT_TMDX_DRIVE_TEDS_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  formType: LegTypeEnum.ground,
  legType: RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
  distanceInMeters: 0,
  isFeasibleForDrive: true,
  tripId: null,
} as BaseTmdxDriveTedsGroundSchemaValues;

export const DEFAULT_SELF_SERVICED_VALUES = {
  ...DEFAULT_LEG_VALUES,
  ...DEFAULT_ROUTE_LEG_VALUES,
  ...DEFAULT_LEG_RESOURCES_VALUES,
  formType: LegTypeEnum.ground,
  legType: RouteLegTypeEnum.SelfServicedGroundLeg,
  distanceInMeters: 0,
  isFeasibleForDrive: true,
} as BaseSelfServicedGroundSchemaValues;

// ACCOMMODATION FORM
export const DEFAULT_ACCOMMODATION_VALUES = {
  ...DEFAULT_LEG_VALUES,
  formType: LegTypeEnum.accommodation,
  segmentType: SegmentTypeEnum.custom,
  legType: RouteLegTypeEnum.AccommodationLeg,
  checkInAtValue: null,
  checkInAt: '',
  checkOutAtValue: null,
  checkOutAt: '',
  hotelName: '',
  rooms: 1,
  bookingNumber: '',
  arrivalLocation: DEFAULT_CUSTOM_LOCATION_VALUES,
  resources: [],
  hasScs: false,
} as BaseAccommodationLegSchemaValues;
